import { Colors } from "@/enums/Colors";
import { useResponsive } from "@/hooks/useResponsive";
import { Box, Typography, styled } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import EmptyScreenSVG from "../../../assets/images/empty-notifications.svg";
import CommonButton from "../utils/CommonButton";
import DataRoomFileUploadModal from "./DataRoomFileUploadModal";
import useFileUploadProgress from "./useFileUploadProgress";

interface DataRoomEmptyManageScreenProps {
  onFileUpload: (files: File[]) => void;
}

function DataRoomEmptyManageScreen(props: DataRoomEmptyManageScreenProps) {
  const { t } = useTranslation("virtualDataRooms");
  const { isMobile, isTablet } = useResponsive();
  const [openFileUploadModal, setOpenFileUploadModal] = useState<boolean>(false);

  const { fileProgressMap, resetFileProgressMap, files, uploadedFiles, setFiles } = useFileUploadProgress();

  return (
    <>
      <MainContainer>
        <EmptyScreenImg alt="empty-screen" src={EmptyScreenSVG.src} isMobile={isMobile} isTablet={isTablet} />
        <Box className="text-container">
          <Typography
            sx={{
              fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
              fontWeight: 600,
              lineHeight: { xs: "20px", md: "24px" },
              textAlign: "center",
            }}
          >
            {t("emptyManageScreen.title")}
          </Typography>
          <Typography
            color={Colors.LIGHT_GRAY_8A8E93}
            sx={{
              fontSize: { xs: "0.875rem", sm: "0.875rem", md: "1rem" },
              fontWeight: 400,
              lineHeight: { xs: "19.6px", md: "22px" },
              textAlign: "center",
            }}
          >
            {t("emptyManageScreen.description")}
          </Typography>
        </Box>
        <CommonButton
          onClick={() => setOpenFileUploadModal(true)}
          purpleBorderVariant
          sx={{
            borderRadius: "0.5rem",
            height: { xs: "2.5rem", sm: "2.25rem", md: "2rem" },
            minWidth: { xs: "200px", sm: "180px", md: "auto" },
            fontSize: { xs: "0.875rem", md: "1rem" },
          }}
        >
          {t("fileUpload.dragDrop.buttons.browse")}
        </CommonButton>
      </MainContainer>
      <DataRoomFileUploadModal
        files={files}
        setFiles={setFiles}
        uploadedFiles={uploadedFiles}
        uploadFunc={props.onFileUpload}
        open={openFileUploadModal}
        setOpen={setOpenFileUploadModal}
        fileProgressMap={fileProgressMap}
        resetFileProgressMap={resetFileProgressMap}
      />
    </>
  );
}

const MainContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "1.983rem 1rem",
  gap: "1rem",
  height: "100%",
  width: "100%",
  maxWidth: "600px",
  margin: "0 auto",
  "& .text-container": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    maxWidth: "400px",
    gap: "0.5rem",
  },
  [theme.breakpoints.up("sm")]: {
    padding: "2rem 1.5rem",
    gap: "1.5rem",
  },
  [theme.breakpoints.up("md")]: {
    padding: "1.983rem 0",
    gap: "1rem",
  },
}));

export const EmptyScreenImg = styled("img", {
  shouldForwardProp: (prop) => prop !== "isMobile" && prop !== "isTablet",
})<{
  isMobile?: boolean;
  isTablet?: boolean;
}>((props) => ({
  width: props.isMobile ? "120px" : props.isTablet ? "180px" : "243px",
  height: props.isMobile ? "120px" : props.isTablet ? "180px" : "243px",
  maxWidth: "100%",
  objectFit: "contain",
}));

export default DataRoomEmptyManageScreen;
